import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { orderService, OrderResponse, OrderStatus } from '../services/OrderService';
import { useLanguage } from '../contexts/LanguageContext';
import { useOrder } from '../contexts/OrderContext';
import { generateOrderId } from '../utils/common';

export interface OrderCreationData {
  name: string;
  vin: string;
  buyer?: string;
  service_type?: string;
  vin_data?: any;
}

export interface UseOrderCreationReturn {
  isCreating: boolean;
  error: string | null;
  createOrder: (data: OrderCreationData) => Promise<OrderResponse | null>;
  clearError: () => void;
}

/**
 * Shared hook for order creation logic
 * Used by both HomePage and OrdersPanelStatic
 */
export const useOrderCreation = (): UseOrderCreationReturn => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { setActiveOrderId } = useOrder();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrder = async (data: OrderCreationData): Promise<OrderResponse | null> => {
    setIsCreating(true);
    setError(null);

    try {
      // Validate VIN
      if (!data.vin || !data.vin.trim()) {
        setError(t('orders.panel.errors.vinMendatory', { defaultValue: 'VIN is mandatory' }));
        return null;
      }

      // Generate transaction IDs
      const transactionId = orderService.generateTransactionId();
      const externalOrderId = generateOrderId();

      // Build order data
      const orderData = {
        name: data.name,
        transaction_id: transactionId,
        vin_data: data.vin_data || null,
        external_order_id: externalOrderId,
        parts_in_order: {
          items: [],
          total_items: 0,
          export_timestamp: new Date().toISOString(),
          vins: [data.vin]
        },
        status: 'draft' as OrderStatus,
        vin: data.vin,
        buyer: data.buyer || "",
        service_type: data.service_type || "",
        language: currentLanguage || "en",
      };

      // Create the order
      const createdOrder = await orderService.createOrder(orderData);

      // Set as active order
      setActiveOrderId(createdOrder.id);

      return createdOrder;
    } catch (err) {
      console.error('Error creating order:', err);
      setError(t('orders.panel.errors.failedToCreate', { defaultValue: 'Failed to create order' }));
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    isCreating,
    error,
    createOrder,
    clearError
  };
};
